/**
 * Talking to relay-server, and the sign-in gate in front of it.
 *
 * ── Same origin, so there is nothing to configure ───────────────────────────
 * Every path here is relative. The Worker forwards `/api/*` to relay-server
 * over a service binding, so the page, the API and the sockets share one
 * origin: the `mauvely_session` cookie rides along on its own, there is no
 * CORS preflight and no token to hold. `credentials: 'same-origin'` is stated
 * rather than left to the default because the default is what somebody would
 * change while chasing something else.
 *
 * ── The gate is one answer, not a guess ─────────────────────────────────────
 * `GET /api/relay/me` is the whole of it. A browser on relay.mauvely.com cannot
 * ask the accounts service on mauvely.com with its cookie, so this route exists
 * to answer for it; 401 means sign in, and nothing else is inspected. Reading
 * the cookie here would be a second, weaker reading of the same fact — the
 * cookie is `HttpOnly`, so it is not readable anyway, and a page that tried
 * would conclude "signed out" for everybody.
 *
 * ── The guest's credential is a header, and it lives here ───────────────────
 * A guest has no account and no cookie. `POST /api/relay/meetings/join` hands
 * back a `meetingToken`, scoped to one meeting with a twelve-hour ceiling, and
 * every later request presents it as `x-relay-meeting-token` — never in a URL,
 * because a credential in a query string is a credential in an access log.
 *
 * It is held **here**, at the one function every request goes through, rather
 * than in a wrapper beside it. A wrapper only covers its own callers, and the
 * SFU signalling, the roster poll and the recording upload all call this
 * directly: a guest whose media requests carried no credential sat at
 * "Joining…" for ever while every surface went on looking right.
 *
 * In memory, never `localStorage`: the link is the whole credential, and a
 * shared computer is the ordinary case for somebody joining from a link in a
 * chat.
 */

import type { Person } from './relay/types';

export const GUEST_HEADER = 'x-relay-meeting-token';

let meetingToken: string | null = null;

export function setMeetingToken(token: string | null): void {
  meetingToken = token;
}

export function currentMeetingToken(): string | null {
  return meetingToken;
}

/** Where sign-in lives. `mauvely.com` in production, website-main's local
 *  Worker in dev — 8787, which is the port the whole local stack resolves
 *  sessions against. */
export function accountUrl(): string {
  const { hostname } = window.location;
  const local = hostname === 'localhost' || hostname === '127.0.0.1';
  return local ? 'http://localhost:8787' : 'https://mauvely.com';
}

/** Sign in, then come back to exactly where they were. */
export function signInUrl(): string {
  const next = encodeURIComponent(window.location.href);
  return `${accountUrl()}/signin?next=${next}`;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorBody {
  /** The suite's error envelope: `{code, message}`. Read as either shape, so
   * an answer that carried a bare code before the envelope was standard still
   * names its code. */
  error?: string | { code?: unknown; message?: unknown };
  message?: string;
}

/**
 * One request, with the error body read.
 *
 * relay-server answers `{error: {code, message}}` and the message is written
 * for a person, so it is what reaches the screen. A caller that catches an
 * `ApiError` and writes its own sentence is writing a second copy of somebody
 * else's copy.
 */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...(meetingToken ? { [GUEST_HEADER]: meetingToken } : {}),
      ...init?.headers
    }
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ErrorBody;
    const error = typeof body.error === 'string' ? { code: body.error } : body.error;
    const code = typeof error?.code === 'string' && error.code ? error.code : 'error';
    const message =
      typeof error?.message === 'string' && error.message
        ? error.message
        : body.message ?? 'Something went wrong.';
    throw new ApiError(res.status, code, message);
  }

  return (await res.json()) as T;
}

interface MeAnswer {
  user: {
    id: string;
    name: string;
    handle: string | null;
    avatarUrl: string | null;
  };
}

/** The signed-in account, or null when the answer was 401. */
export async function fetchMe(): Promise<Person | null> {
  try {
    const { user } = await api<MeAnswer>('/api/relay/me');
    return {
      id: user.id,
      name: user.name,
      ...(user.handle ? { handle: user.handle } : {}),
      ...(user.avatarUrl ? { avatarUrl: user.avatarUrl } : {}),
      presence: 'online'
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}
